import { putLockCompany, putUnlockCompany } from "@/services/Company";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { Grid } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Company } from "@/types";
import Text from "@/components/atoms/Text";
import Checkbox from "@/components/atoms/Checkbox";
import CTA from "@/components/atoms/CTA";

interface CompanyDrawerProps {
  company: Company;
}

export default function SettingsTab({ company }: CompanyDrawerProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync: lockCompany } = useMutation({
    mutationFn: (companyId: number) => putLockCompany(companyId),
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:lockCompany.companyLocked"))
      );
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] });
    },
    onError: (error) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:lockCompany.companyLockError"))
      );
    },
  });

  const { mutateAsync: unlockCompany } = useMutation({
    mutationFn: (companyId: number) => putUnlockCompany(companyId),
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:lockCompany.companyUnlocked"))
      );
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] });
    },
    onError: (error) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:lockCompany.companyUnlockError"))
      );
    },
  });

  return (
    <Grid p="lg" align="center">
      <Grid.Col span={3}>
        <Text color="secondary" size="sm">
          {t("commonAdmin:tradeLock")}
        </Text>
      </Grid.Col>
      <Grid.Col span={1}>
        <Checkbox checked={company.isLocked} />
      </Grid.Col>
      <Grid.Col span={8}>
        <CTA onClick={() => (company.isLocked ? unlockCompany(company.id) : lockCompany(company.id))}>
          {company.isLocked ? t("commonAdmin:lockCompany.unlockCompany") : t("commonAdmin:lockCompany.lockCompany")}
        </CTA>
      </Grid.Col>
    </Grid>
  );
}
